/**
 * App-wide Cmd-K hybrid search (server-side crmSearchApi).
 * Keeps existing HubQuickSearch (Space) page-local search unchanged.
 * CRM entity hits keep normal sections; Email / Drive / Dropbox are source-grouped.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HardDrive, Mail, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../utils/AuthContext';
import { crmAuthedFetch } from '../../utils/crmAuthedFetch';
import { eventTargetIsEditable, isHubQuickSearchGloballyBlocked } from '../../utils/isEditableTarget';
import { canUseCrmSearch } from '../../utils/searchRoleVisibility';
import { searchHitHref } from '../../utils/searchHitHref';
import { OPEN_CRM_SEARCH_EVENT } from './crmSearchEvents';
import './HubQuickSearch.css';

type SearchHit = {
  id: string;
  entityType: string;
  title: string;
  snippet: string;
  path: string;
  score: number;
  sources?: string[];
  externalSource?: 'email' | 'drive' | 'dropbox' | null;
  openInSourceUrl?: string | null;
  sourcePath?: string | null;
  sourceSenderOrFolder?: string | null;
  sourceDate?: string | null;
};

type SourceStatus = { source: string; status: string; note?: string };

type FlatRow =
  | { kind: 'section'; key: string; label: string }
  | { kind: 'hit'; key: string; hit: SearchHit; hitIndex: number };

function isStaffSearchUser(user: { role?: string; is1099Contractor?: boolean } | null | undefined): boolean {
  return canUseCrmSearch(user?.role, { is1099Contractor: user?.is1099Contractor === true });
}

function sourceSectionLabel(source: string): string {
  if (source === 'email') return 'Email';
  if (source === 'drive') return 'Drive';
  if (source === 'dropbox') return 'Dropbox';
  return source;
}

function SourceIcon({ source }: { source?: string | null }) {
  if (source === 'email') return <Mail size={16} />;
  if (source === 'drive' || source === 'dropbox') return <HardDrive size={16} />;
  return <Search size={16} />;
}

function hitMetaLine(hit: SearchHit): string | null {
  const parts = [
    hit.sourceSenderOrFolder,
    hit.sourcePath && hit.sourcePath !== hit.sourceSenderOrFolder ? hit.sourcePath : null,
    hit.sourceDate,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

function buildFlatRows(hits: SearchHit[]): FlatRow[] {
  const crm: SearchHit[] = [];
  const byExternal: Record<string, SearchHit[]> = { email: [], drive: [], dropbox: [] };
  for (const hit of hits) {
    const ext = hit.externalSource;
    if (ext === 'email' || ext === 'drive' || ext === 'dropbox') {
      byExternal[ext].push(hit);
    } else {
      crm.push(hit);
    }
  }
  const rows: FlatRow[] = [];
  let hitIndex = 0;
  if (crm.length) {
    rows.push({ kind: 'section', key: 'crm', label: 'CRM' });
    for (const hit of crm) {
      rows.push({ kind: 'hit', key: hit.id, hit, hitIndex: hitIndex++ });
    }
  }
  for (const source of ['email', 'drive', 'dropbox'] as const) {
    const list = byExternal[source];
    if (!list.length) continue;
    rows.push({ kind: 'section', key: source, label: sourceSectionLabel(source) });
    for (const hit of list) {
      rows.push({ kind: 'hit', key: hit.id, hit, hitIndex: hitIndex++ });
    }
  }
  return rows;
}

const GlobalCrmSearch: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [sourcesStatus, setSourcesStatus] = useState<SourceStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<number | null>(null);

  const enabled = isAuthenticated && isStaffSearchUser(user);
  const flatRows = useMemo(() => buildFlatRows(hits), [hits]);
  const hitRows = useMemo(() => flatRows.filter((r): r is Extract<FlatRow, { kind: 'hit' }> => r.kind === 'hit'), [flatRows]);

  useEffect(() => {
    if (!enabled) return;
    const onOpen = () => setOpen(true);
    window.addEventListener(OPEN_CRM_SEARCH_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_CRM_SEARCH_EVENT, onOpen);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'k') return;
      if (eventTargetIsEditable(e) && !open) return;
      if (isHubQuickSearchGloballyBlocked() && !open) return;
      e.preventDefault();
      setOpen((v) => !v);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled, open]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setHits([]);
    setSourcesStatus([]);
    setError(null);
    setActiveIndex(0);
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const runSearch = useCallback(async (q: string) => {
    const trimmed = q.trim();
    if (!trimmed) {
      setHits([]);
      setSourcesStatus([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Optional local read-only server (see docs/LOCAL_SEARCH_TEST.md).
      // When set, Cmd-K hits localhost instead of the deployed crmSearchApi.
      const localUrl = (process.env.REACT_APP_LOCAL_SEARCH_URL || '').replace(/\/$/, '');
      let res: Response;
      if (localUrl) {
        res = await fetch(`${localUrl}/search`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'search',
            query: trimmed,
            limit: 25,
            as: process.env.REACT_APP_LOCAL_SEARCH_AS || 'admin',
          }),
        });
      } else {
        res = await crmAuthedFetch('crmSearchApi', {
          method: 'POST',
          body: JSON.stringify({ action: 'search', query: trimmed, limit: 25 }),
        });
      }
      const json = await res.json();
      if (!res.ok || !json.success) {
        setError(json.error || 'Search failed');
        setHits([]);
        setSourcesStatus([]);
      } else {
        setHits(Array.isArray(json.hits) ? json.hits : []);
        setSourcesStatus(Array.isArray(json.sourcesStatus) ? json.sourcesStatus : []);
      }
    } catch (err) {
      setError((err as Error).message || 'Search failed');
      setHits([]);
      setSourcesStatus([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      void runSearch(query);
    }, 220);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [query, open, runSearch]);

  const onSelect = useCallback(
    (hit: SearchHit) => {
      setOpen(false);
      if (hit.openInSourceUrl && /^https?:\/\//i.test(hit.openInSourceUrl)) {
        window.open(hit.openInSourceUrl, '_blank', 'noopener,noreferrer');
        return;
      }
      if (hit.path && /^https?:\/\//i.test(hit.path)) {
        window.open(hit.path, '_blank', 'noopener,noreferrer');
        return;
      }
      if (hit.path) navigate(searchHitHref(hit.path));
    },
    [navigate],
  );

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, Math.max(0, hitRows.length - 1)));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(0, i - 1));
        return;
      }
      if (e.key === 'Enter' && hitRows[activeIndex]) {
        e.preventDefault();
        onSelect(hitRows[activeIndex].hit);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, hitRows, activeIndex, onSelect]);

  useEffect(() => {
    if (!listRef.current) return;
    const active = listRef.current.querySelector('.hqs-item.active');
    active?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  if (!enabled || !open) return null;

  const notConnected = sourcesStatus.filter((s) => s.status === 'not_connected' || s.status === 'off');

  return (
    <div className="hqs-overlay" role="presentation" onClick={() => setOpen(false)}>
      <div
        className="hqs-panel"
        role="dialog"
        aria-modal="true"
        aria-label="CRM search"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="hqs-input-row">
          <Search size={18} className="hqs-input-icon" />
          <input
            ref={inputRef}
            type="search"
            className="hqs-input"
            placeholder="Search CRM…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search CRM"
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="hqs-kbd">esc</kbd>
        </div>
        <div className="hqs-hint">
          <kbd>⌘</kbd>/<kbd>Ctrl</kbd>+<kbd>K</kbd> · server search · respects your role
          {loading ? ' · searching…' : ''}
        </div>
        {error && (
          <div className="hqs-hint" style={{ color: '#b91c1c' }}>{error}</div>
        )}
        <div className="hqs-results pm-scroll" ref={listRef}>
          {hits.length === 0 && !loading && query.trim() && !error ? (
            <div className="hqs-empty">No matches</div>
          ) : (
            flatRows.map((row) => {
              if (row.kind === 'section') {
                return (
                  <div key={`sec-${row.key}`} className="hqs-hint" style={{ fontWeight: 600, marginTop: 8 }}>
                    {row.label}
                  </div>
                );
              }
              const { hit, hitIndex } = row;
              const meta = hitMetaLine(hit);
              return (
                <button
                  key={hit.id}
                  type="button"
                  className={`hqs-item ${hitIndex === activeIndex ? 'active' : ''}`}
                  onMouseEnter={() => setActiveIndex(hitIndex)}
                  onClick={() => onSelect(hit)}
                >
                  <span className="hqs-item-icon">
                    <SourceIcon source={hit.externalSource || hit.entityType} />
                  </span>
                  <span className="hqs-item-text">
                    <span className="hqs-item-title">{hit.title}</span>
                    {hit.snippet ? <span className="hqs-item-sub">{hit.snippet}</span> : null}
                    {meta ? <span className="hqs-item-sub">{meta}</span> : null}
                  </span>
                  <span className="hqs-item-kind">
                    {hit.externalSource
                      ? sourceSectionLabel(hit.externalSource)
                      : hit.entityType}
                  </span>
                </button>
              );
            })
          )}
          {notConnected.length > 0 && query.trim() && !loading ? (
            <div className="hqs-hint" style={{ marginTop: 8 }}>
              {notConnected.map((s) => (
                <div key={s.source}>
                  {sourceSectionLabel(s.source)}: {s.status === 'not_connected' ? 'not connected' : 'off'}
                </div>
              ))}
            </div>
          ) : null}
        </div>
        <div className="hqs-footer">
          <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
          <span><kbd>Enter</kbd> open</span>
          <span><kbd>Esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
};

export default GlobalCrmSearch;
